import { SummaryDraft, SummaryDraftConflictError, SummaryDraftStore } from './summaryDraftStore';

const sends = new Map<string, Promise<void>>();
async function sendInOrder(key: string | undefined, send: () => Promise<void>) {
    if (!key) return send();
    const pending = (sends.get(key) ?? Promise.resolve()).catch(() => {}).then(async () => {
        if (globalThis.navigator?.locks) await navigator.locks.request(`notizy:summary-save:${key}`, send);
        else await send();
    });
    sends.set(key, pending);
    try { await pending; } finally { if (sends.get(key) === pending) sends.delete(key); }
}

/** One server request at a time, with bounded, batched local checkpoints. */
export class SummarySaveQueue {
    private capture?: () => { content: string; document?: object; stream?: SummaryDraft['stream'] };
    private checkpoint?: ReturnType<typeof setTimeout>;
    private latest: SummaryDraft | null = null;
    private running: Promise<boolean> | null = null;
    private writing: Promise<void> = Promise.resolve();
    private idle?: ReturnType<typeof setTimeout>;
    private maximum?: ReturnType<typeof setTimeout>;
    private listeners = new Set<() => void>();
    private persisted: SummaryDraft | null = null;
    private readable = true;
    private attached = true;
    private known = new Set<string>();
    private uncertain = false;
    suspended = false;
    loaded = false;
    restored = false;
    failed = false;
    storageFailed = false;
    storageConflict = false;
    competingDraft: SummaryDraft | null = null;
    conflict = false;
    readonly ready: Promise<void>;

    constructor(public confirmed: string, private send: (content: string) => Promise<void>, private store?: SummaryDraftStore, private coordinationKey?: string) {
        this.known.add(confirmed);
        this.loaded = !store;
        this.ready = store ? store.read().then(draft => {
            if (draft && (draft.content !== confirmed || draft.uncertain)) {
                this.latest = this.persisted = draft;
                this.uncertain = !!draft.uncertain;
                this.conflict = draft.base !== confirmed || this.uncertain;
                this.restored = true;
            } else if (draft) { this.persisted = draft; this.persist(); }
        }).catch(() => { this.storageFailed = true; this.readable = false; })
            .finally(() => { this.loaded = true; this.emit(); }) : Promise.resolve();
    }
    get content() { return this.latest?.content ?? this.confirmed; }
    get document() { return this.latest?.document; }
    get stream() { return this.latest?.stream; }
    get draft() { return this.latest; }
    get pending() { return !!this.capture || this.latest !== null || this.running !== null; }
    get saving() { return this.running !== null; }
    get durable() { return !this.capture && !this.storageFailed && this.latest === this.persisted; }
    subscribe(listener: () => void) {
        this.listeners.add(listener);
        return () => { this.listeners.delete(listener); };
    }
    attach() { this.attached = true; }
    detach() { this.materialize(); this.attached = false; this.cancelTimers(); }
    private emit() { this.listeners.forEach(listener => listener()); }
    private persist() {
        if (!this.store || !this.readable) return;
        this.writing = this.writing.then(async () => {
            const target = this.latest;
            if (target === this.persisted || this.storageConflict) return;
            try {
                await this.store!.write(target);
                this.persisted = target; this.storageFailed = false;
            } catch (error) {
                this.storageFailed = true;
                if (error instanceof SummaryDraftConflictError) {
                    this.conflict = true; this.storageConflict = true;
                    try { this.competingDraft = await this.store!.read(); this.persisted = this.competingDraft; }
                    catch { this.readable = false; }
                }
            }
            this.emit();
        });
    }
    async settleLocal() { this.materialize(); await this.ready; await this.writing; return this.durable; }
    /** Stop new uploads and wait for one already sent before deleting the report. */
    async pause() {
        this.suspended = true; this.cancelTimers(); this.materialize();
        await this.running;
        await this.settleLocal();
    }
    resume() { this.suspended = false; this.schedule(); this.emit(); }
    /** Called only after confirmed server deletion, including a conflicting local draft. */
    async discard() {
        await this.pause();
        if (this.store && this.readable) {
            try { this.persisted = await this.store.read(); } catch { this.storageFailed = true; }
        }
        this.storageConflict = false; this.competingDraft = null;
        this.endStream(); this.adopt(''); this.detach();
        await this.settleLocal();
    }
    /** Retain an immutable editor document; serialize once for the latest edit in a batch. */
    defer(capture: () => { content: string; document?: object; stream?: SummaryDraft['stream'] }) {
        if (!this.loaded) return;
        const first = !this.capture;
        this.capture = capture;
        this.checkpoint ??= setTimeout(() => this.materialize(), 250);
        this.schedule();
        if (first) this.emit();
    }
    materialize() {
        const capture = this.capture;
        this.capture = undefined;
        clearTimeout(this.checkpoint); this.checkpoint = undefined;
        if (capture) {
            const snapshot = capture();
            this.setDraft(snapshot.content, snapshot.document, snapshot.stream);
        }
    }
    stage(content: string, document?: object, stream?: SummaryDraft['stream']) {
        if (!this.loaded) return;
        this.capture = undefined;
        clearTimeout(this.checkpoint); this.checkpoint = undefined;
        this.setDraft(content, document, stream);
        this.schedule();
    }
    private setDraft(content: string, document?: object, stream?: SummaryDraft['stream']) {
        this.latest = !this.running && content === this.confirmed && !this.uncertain ? null : {
            version: 1, base: this.conflict && this.latest ? this.latest.base : this.confirmed, content, document, uncertain: this.uncertain, stream,
        };
        this.failed = false;
        this.persist();
        this.emit();
    }
    private schedule() {
        if (!this.suspended && this.attached && !this.conflict) {
            clearTimeout(this.idle);
            this.idle = setTimeout(() => { void this.flush(); }, 1000);
            this.maximum ??= setTimeout(() => { void this.flush(); }, 10000);
        }
    }
    cancelTimers() {
        clearTimeout(this.idle); clearTimeout(this.maximum);
        this.idle = this.maximum = undefined;
    }
    /** During AI work, checkpoint recoverable user edits without uploading previews. */
    beginStream() { this.materialize(); this.suspended = true; this.uncertain = true; this.cancelTimers(); this.stage(this.content, this.document); }
    endStream() { this.suspended = false; this.uncertain = false; }
    /** An external version must never replace a pending local edit silently. */
    reconcile(content: string) {
        if (this.known.has(content)) return;
        if (this.latest || this.capture) { this.conflict = true; this.cancelTimers(); this.emit(); }
        else { this.confirmed = content; this.known.add(content); }
    }
    adopt(content: string) {
        this.capture = undefined;
        clearTimeout(this.checkpoint); this.checkpoint = undefined;
        this.confirmed = content; this.known.add(content);
        if (this.known.size > 12) this.known.delete(this.known.values().next().value!);
        this.latest = null; this.failed = false; this.conflict = false;
        this.cancelTimers(); this.persist(); this.emit();
    }
    /** Only invoked after the user has compared the two versions. */
    resolve(content: string, keepLocal: boolean) {
        this.materialize();
        const local = this.latest;
        this.storageConflict = false; this.competingDraft = null;
        this.endStream(); this.adopt(content); this.restored = false;
        if (keepLocal && local) this.stage(local.content, local.document);
    }
    /** Rebase a verified, non-overlapping merge on the freshly read server version. */
    rebase(server: string, merged: string, document?: object) {
        this.endStream(); this.adopt(server); this.restored = true;
        if (merged !== server) this.stage(merged, document);
    }
    flush(): Promise<boolean> {
        this.materialize();
        this.cancelTimers();
        if (this.running) return this.running;
        if (this.suspended || this.conflict || !this.attached) return Promise.resolve(false);
        if (this.loaded && this.latest === null) return Promise.resolve(true);
        this.running = Promise.resolve().then(async () => {
            if (!this.loaded) await this.ready;
            while (this.latest !== null || this.capture) {
                this.materialize();
                if (this.suspended || this.conflict || !this.attached) return false;
                if (this.store) await this.settleLocal();
                if (this.conflict) return false;
                const target = this.latest;
                if (!target) break;
                try {
                    if (target.content !== this.confirmed) await sendInOrder(this.coordinationKey, () => this.send(target.content));
                    this.confirmed = target.content; this.known.add(target.content);
                    if (this.latest === target) this.latest = null;
                    else if (this.latest) this.latest = { ...this.latest, base: this.confirmed };
                    this.failed = false; this.persist();
                } catch (error) {
                    if (error instanceof SummaryDraftConflictError) { this.conflict = true; this.emit(); return false; }
                    if (this.latest !== target) continue;
                    this.failed = true; return false;
                }
            }
            if (this.store) await this.settleLocal();
            return true;
        }).finally(() => { this.running = null; this.emit(); });
        this.emit();
        return this.running;
    }
}
